/**
 * Runtime configuration loader.
 *
 * In deployed environments: loads API_URL from /config.json (generated at
 * container startup from ConfigMap). Fails loudly if config is missing.
 *
 * In local development: falls back to VITE_API_URL from .env, or `/api`
 * so the Vite dev proxy works without extra local env wiring.
 *
 * Two local-dev recipes — they target the API differently:
 *   1. Local backend (docker-compose): VITE_API_URL=http://localhost:8010/api
 *      talks DIRECTLY to :8010 and bypasses the Vite proxy. The committed
 *      .env does this. If nothing listens on :8010 every call gets
 *      ERR_CONNECTION_REFUSED (see api/client.ts networkUnreachableMessage).
 *   2. Remote API via the proxy: VITE_API_URL=/api PROXY_API_TARGET=https://<env-host>
 *      routes /api through vite.config.ts's proxy to a remote backend — use
 *      this to run the dev server (e.g. for screenshots) without a local API.
 */
import { reportError } from './lib/errors/reportError';

interface RuntimeConfig {
  API_URL: string;
  SENTRY_DSN?: string;
  SENTRY_ENVIRONMENT?: string;
  /**
   * Enable staff offline sync mode: IndexedDB read cache, write queue,
   * prefetch, and offline UI. Defaults to false in deployed builds.
   *
   * This does not control /sw.js registration, installed-app behavior, or push.
   */
  OFFLINE_PWA_ENABLED?: boolean;
  /**
   * Oldest app version (repo VERSION, dotted) still considered safe to run. A
   * tab on a build below it reloads immediately — the SU incident lever, flipped
   * with one ConfigMap value and no code change.
   *
   * Absent or null means DISABLED: there is no sentinel value and no default.
   */
  MIN_SUPPORTED_VERSION?: string | null;
}

let cachedConfig: RuntimeConfig | null = null;
let configPromise: Promise<RuntimeConfig> | null = null;

function buildDevFallbackConfig(): RuntimeConfig {
  const apiUrl = import.meta.env.VITE_API_URL || '/api';
  // A relative `/api` goes through the Vite proxy; an absolute URL talks to that
  // host directly and bypasses the proxy. Spell out which one is in effect so an
  // unreachable direct host (the usual cause of a surprise login screen) is
  // diagnosable from the console alone.
  const viaProxy = apiUrl.startsWith('/');
  console.log(
    `[Config] Dev mode: API_URL=${apiUrl}`,
    viaProxy ? '(via Vite proxy)' : '(direct host, bypassing the Vite proxy)',
  );
  return {
    API_URL: apiUrl,
    ...(import.meta.env.VITE_SENTRY_DSN && { SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN }),
    ...(import.meta.env.VITE_SENTRY_ENVIRONMENT && { SENTRY_ENVIRONMENT: import.meta.env.VITE_SENTRY_ENVIRONMENT }),
  };
}

function throwConfigLoadError(message: string, error: unknown, extra?: Record<string, unknown>): never {
  const wrappedError = new Error(message, { cause: error instanceof Error ? error : undefined });

  reportError(error instanceof Error ? error : wrappedError, 'config.fetch', {
    userMessage: 'Configuration load failed — please refresh',
    extra,
  });

  throw wrappedError;
}

/**
 * Load runtime config from /config.json.
 * Caches the result for subsequent calls.
 * Throws in production if config cannot be loaded.
 */
export async function loadConfig(): Promise<RuntimeConfig> {
  if (cachedConfig) {
    return cachedConfig;
  }

  if (configPromise) {
    return configPromise;
  }

  configPromise = (async () => {
    try {
      // eslint-disable-next-line no-restricted-globals -- the frontend's own runtime config, not the API
      const response = await fetch('/config.json');

      if (!response.ok) {
        if (import.meta.env.DEV) {
          cachedConfig = buildDevFallbackConfig();
          return cachedConfig;
        }

        return throwConfigLoadError(
          '[Config] /config.json returned non-OK status. ' +
            'The app cannot determine its API URL. Ensure the ConfigMap and ' +
            'container entrypoint are generating /config.json correctly.',
          new Error(`HTTP ${response.status}`),
          { status: response.status },
        );
      }

      const config = await response.json();
      if (!config.API_URL) {
        if (import.meta.env.DEV) {
          cachedConfig = buildDevFallbackConfig();
          return cachedConfig;
        }

        return throwConfigLoadError(
          '[Config] Failed to load /config.json and no dev fallback available. ' +
            'The app cannot determine its API URL. Ensure the ConfigMap and ' +
            'container entrypoint are generating /config.json correctly.',
          new Error('[Config] /config.json loaded but API_URL is missing'),
        );
      }

      cachedConfig = config;
      console.log('[Config] Loaded runtime config:', config.API_URL);
      return config;
    } catch (error) {
      if (import.meta.env.DEV) {
        cachedConfig = buildDevFallbackConfig();
        return cachedConfig;
      }

      return throwConfigLoadError(
        '[Config] Failed to load /config.json and no dev fallback available. ' +
          'The app cannot determine its API URL. Ensure the ConfigMap and ' +
          'container entrypoint are generating /config.json correctly.',
        error,
      );
    }
  })();

  return configPromise;
}

/**
 * Get API URL synchronously. Must only be called after initConfig() resolves.
 * Throws if config has not been loaded yet.
 */
export function getApiUrl(): string {
  if (cachedConfig) {
    return cachedConfig.API_URL;
  }
  throw new Error(
    '[Config] getApiUrl() called before config was loaded. ' +
    'Ensure initConfig() has resolved before importing API modules.'
  );
}

export function getSentryDsn(): string | undefined {
  return cachedConfig?.SENTRY_DSN || undefined;
}

export function getSentryEnvironment(): string | undefined {
  return cachedConfig?.SENTRY_ENVIRONMENT || undefined;
}

/**
 * Check if staff offline sync mode is enabled in runtime config.
 *
 * This gates the staff offline data/UX path. It is not a service-worker
 * kill switch: /sw.js also owns app updates, guest-portal caching, and push.
 * Defaults to false when not set. In dev mode, defaults to true.
 */
export function isOfflinePwaEnabled(): boolean {
  if (cachedConfig?.OFFLINE_PWA_ENABLED != null) {
    return cachedConfig.OFFLINE_PWA_ENABLED;
  }
  // Default: enabled in dev, disabled in deployed environments until explicitly enabled
  return import.meta.env.DEV;
}

/** Normalize the raw config key: only a non-empty string enables the check. */
function readMinSupportedVersion(config: RuntimeConfig | null): string | null {
  const value = config?.MIN_SUPPORTED_VERSION;
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Read MIN_SUPPORTED_VERSION from `/config.json`, bypassing the boot-time memo
 * above. `null` means the ConfigMap does not set it and the check is off.
 *
 * Deliberately NOT served from `cachedConfig`: an already-open tab cached its
 * config at boot, so reading the memo would never see the ConfigMap flip that
 * declares its build unsafe. `lib/updateLifecycle.ts` polls this. It reads only
 * this one key — `API_URL` and the Sentry keys stay pinned to whatever the tab
 * booted with, because swapping those under a running page is a different and
 * much larger change.
 *
 * Rejects on a failed fetch or unparseable body. The caller decides what a
 * failed poll means; this does not guess, because guessing here either strands a
 * user on an unsafe build or force-reloads them for a network blip.
 */
export async function fetchMinSupportedVersion(): Promise<string | null> {
  // eslint-disable-next-line no-restricted-globals -- the frontend's own runtime config, not the API
  const response = await fetch('/config.json', { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`[Config] /config.json poll returned HTTP ${response.status}`);
  }
  const config = (await response.json()) as RuntimeConfig;
  return readMinSupportedVersion(config);
}

/**
 * Initialize config at app startup.
 * Call this in main.tsx before rendering. Throws if config cannot be loaded.
 */
export async function initConfig(): Promise<void> {
  await loadConfig();
}
